import type { ControlType } from '@pedestal-panels/contracts';

/**
 * Mapping from canonical `ControlType` (the taxonomy in
 * `@pedestal-panels/contracts`) to the custom-element tag name the
 * primitive registers under. Panel compositions iterate their
 * inventory and dispatch each control through this map.
 *
 * The map is intentionally exhaustive at the type level so a new
 * control type added to the taxonomy without a corresponding primitive
 * surfaces as a TypeScript error at compile time, not as a silent
 * blank in the rendered panel.
 *
 * Where multiple control types share a primitive (e.g. all rotary
 * variants render through `<rotary-encoder>` with differing attributes),
 * the same tag name is reused; the consuming panel composition is
 * responsible for setting the appropriate attributes per type.
 */
export const controlTypeToTag = {
  // Rotary
  'rotary-encoder-detented': 'rotary-encoder',
  'rotary-encoder-continuous': 'rotary-encoder',
  'rotary-encoder-with-push': 'rotary-encoder',
  'rotary-encoder-with-illumination-ring': 'rotary-encoder',
  'potentiometer': 'rotary-encoder',
  'knob-with-pointer': 'rotary-encoder',
  'knob-skirted': 'rotary-encoder',

  // Linear
  'fader-linear': 'panel-fader',
  'fader-with-centre-detent': 'panel-fader',
  'lever-tbar': 'panel-lever',
  'slide-switch': 'panel-lever',

  // Push
  'push-momentary': 'push-button',
  'push-latching': 'push-button',
  'push-exclusive-group': 'push-button',
  'push-illuminated': 'push-button',
  'push-with-display': 'push-button',
  'key-switch': 'push-button',

  // Mechanical switches
  'toggle-switch': 'push-button',
  'rocker-switch': 'push-button',
  'selector-rotary': 'rotary-encoder',

  // Joysticks and axis controls
  'joystick-2axis': 'shading-joystick',
  'joystick-2axis-with-push': 'shading-joystick',
  'joystick-3axis': 'shading-joystick',
  'joystick-3axis-with-push': 'shading-joystick',
  'shading-joystick': 'shading-joystick',
  'ptz-joystick': 'shading-joystick',

  // Trackball
  'trackball': 'rotary-encoder',
  'trackball-with-ring': 'rotary-encoder',

  // Stacked rotary (CGP-500 paint section) — its own primitive renders
  // the dual-concentric cap geometry; the registry routes the canonical
  // control-type to the matching custom element.
  'stacked-dual-ring-rotary': 'stacked-rotary',

  // Displays
  'display-segment': 'panel-display',
  'display-dot-matrix': 'panel-display',
  'display-lcd-character': 'panel-display',
  'display-lcd-graphic': 'panel-display',
  'display-oled-monochrome': 'panel-display',
  'display-oled-colour': 'panel-display',
  'display-led-bargraph': 'panel-display',

  // Indicators
  'led-discrete': 'tally-lamp',
  'led-rgb': 'tally-lamp',
  'lamp-incandescent': 'tally-lamp',
  'meter-vu': 'panel-display',
  'meter-led-bargraph': 'panel-display',

  // Special
  'jog-shuttle': 'rotary-encoder',
  'chassis-feature': 'chassis-feature-shape',
} as const satisfies Record<ControlType, string>;

export type RenderedControlTag = (typeof controlTypeToTag)[keyof typeof controlTypeToTag];

export function tagForControlType(type: ControlType): RenderedControlTag {
  return controlTypeToTag[type];
}
